import { compareAsc, endOfDay, isSameDay, isSameMonth, isSameYear, startOfDay } from "date-fns";
import { useMemo } from "react";
import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { CalendarContext } from "./calendar.context";

const CALENDAR_VIEW_TYPES = {
	year: "year",
	month: "month",
	day: "day",
};

export const InvoiceContext = createContext({
	invoices: null,
	shownInvoices: null,
	newRows: null,
	filteredNewRows: null,
	addNewRow: () => {},
	removeNewRow: () => {},
	removeRow: () => {},
	changeOnNewRow: () => {},
	save: () => {},
	isDayFilled: () => {},
	calculateBalanceChange: () => {},
});

// DELETE START
const ROWS = [
	{ Date: new Date(), Explanation: "1", Revenue: "400", Expense: "100" },
	{ Date: new Date(), Explanation: "2", Revenue: "450", Expense: "100" },
	{ Date: new Date(), Explanation: "3", Revenue: "500", Expense: "100" },
	{ Date: new Date(), Explanation: "4", Revenue: "600", Expense: "100" },
	{ Date: new Date(), Explanation: "5", Revenue: "700", Expense: "100" },
	{ Date: new Date(), Explanation: "6", Revenue: "900", Expense: "100" },
	{ Date: "08/10/2022", Explanation: "7", Revenue: "1000", Expense: "100" },
	{ Date: "08/10/2022", Explanation: "7", Revenue: "1000", Expense: "100" },
	{ Date: "08/10/2022", Explanation: "7", Revenue: "1000", Expense: "100" },
	{ Date: "08/10/2022", Explanation: "7", Revenue: "1000", Expense: "100" },
];
// DELETE END

const InvoiceProvider = ({ children }) => {
	const { selectedDay, shownMonth, shownYear, calendarViewType } = useContext(CalendarContext);

	const [invoices, setInvoices] = useState(ROWS);
	const [shownInvoices, setShownInvoices] = useState([]);
	const [newRows, setNewRows] = useState([]);
	const [startingBalance, setStartingBalance] = useState(1000);

	useEffect(() => {
		var date = new Date();
		switch (calendarViewType) {
			case CALENDAR_VIEW_TYPES.day:
				date = new Date(selectedDay);
				setShownInvoices(invoices.filter((row) => isSameDay(new Date(row.Date), date)));
				break;
			case CALENDAR_VIEW_TYPES.month:
				date = new Date(shownYear, shownMonth, 1);
				setShownInvoices(invoices.filter((row) => isSameMonth(new Date(row.Date), date)));
				break;
			case CALENDAR_VIEW_TYPES.year:
				date = new Date(shownYear, 0, 1);
				setShownInvoices(invoices.filter((row) => isSameYear(new Date(row.Date), date)));
				break;
			default:
				break;
		}
		// }, [selectedDay, shownMonth, shownYear]);
	}, [selectedDay, shownMonth, shownYear, calendarViewType, invoices]);

	const filteredNewRows = useMemo(
		() => newRows.filter((row) => row.Date && (row.Revenue || row.Expense)),
		[newRows]
	);

	const addNewRow = () => {
		setNewRows((initialRows) => {
			const newRows = [
				{ Date: new Date(selectedDay), Explanation: null, Revenue: null, Expense: null },
				...initialRows,
			];
			return newRows;
		});
	};

	const removeNewRow = (index) => {
		setNewRows((initialRows) => {
			const newRows = [...initialRows];
			newRows.splice(index, 1);
			return newRows;
		});
	};

	const removeRow = (index) => {
		// TODO: delete row
		setInvoices((initialRows) => {
			const tempRows = [...initialRows];
			tempRows.splice(index, 1);
			return tempRows;
		});
	};

	const changeOnNewRow = (event, index) => {
		if (event?.target == null) {
			setNewRows((initialRows) => {
				const tempRows = [...initialRows];
				tempRows[index].Date = event;
				return tempRows;
			});
			return;
		}

		const { name, value } = event.target;
		setNewRows((initialRows) => {
			const tempRows = [...initialRows];
			tempRows[index][name] = value;
			return tempRows;
		});
	};

	const save = () => {
		const newBalance = filteredNewRows.reduce((prevValue, currentValue) => {
			return prevValue + ((currentValue?.Revenue ?? 0) - (currentValue?.Expense ?? 0));
		}, startingBalance);

		setNewRows([]);

		if (filteredNewRows.length === 0) return;

		if (newBalance < 0) return;

		setInvoices((initialRows) => [...filteredNewRows, ...initialRows]);
	};

	const isDayFilled = (day) => {
		return invoices.some((row) => {
			return isSameDay(new Date(row.Date), day);
		});
	};

	const calculateBalanceChange = useCallback(
		(start, end) => {
			var balance = 0;
			invoices.forEach((row) => {
				const date = new Date(row.Date);
				const startDate = startOfDay(new Date(start));
				const endDate = endOfDay(new Date(end));
				if (compareAsc(startDate, date) !== 1 && compareAsc(date, endDate) !== 1) {
					balance += row.Revenue - row.Expense;
				}
			});
			return balance;
		},
		[invoices]
	);

	const value = {
		invoices,
		shownInvoices,
		newRows,
		filteredNewRows,
		addNewRow,
		removeNewRow,
		removeRow,
		changeOnNewRow,
		save,
		isDayFilled,
		calculateBalanceChange,
	};
	return <InvoiceContext.Provider value={value}>{children}</InvoiceContext.Provider>;
};

export default InvoiceProvider;
